import { useEffect, useRef } from 'preact/hooks';
import type { Theme } from '../styles/theme.js';
import { MessageBubble } from './MessageBubble.js';
import type { Message, AiMode, AvailabilityState } from '../state/store.js';
import { STRINGS } from '../strings.js';
import { deriveAckState } from '../lib/ackStatus.js';

type Props = {
  messages: Message[];
  aiThinking: boolean;
  aiMode: AiMode;
  availability: AvailabilityState;
  theme: Theme;
  // Incrémenté par ChatWindow à chaque redimensionnement du
  // visualViewport (ouverture/fermeture du clavier iOS) pour recoller la
  // liste en bas — évite de dépendre d'un focus/scrollIntoView séparé.
  scrollTrigger?: number;
  onRetry?: ((localId: string) => void) | undefined;
};

// Message d'accueil du mode humain : purement local/affiché, jamais stocké
// ni persisté (contrat 2.2 : "premier message affiché côté widget (local,
// non stocké)").
const WELCOME_MESSAGE: Message = {
  id: 'welcome-local',
  role: 'system',
  content: STRINGS.welcomeHumanMode,
  createdAt: 0,
};

export function MessageList({
  messages,
  aiThinking,
  aiMode,
  availability,
  theme,
  scrollTrigger,
  onRetry,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const ackState = deriveAckState({ messages, aiMode, availability });

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  }, [messages.length, aiThinking, scrollTrigger, ackState]);

  const showWelcome = aiMode === 'off';

  return (
    <div
      ref={ref}
      style={{
        flex: '1',
        minHeight: '0',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        padding: '12px 16px',
      }}
    >
      {showWelcome && <MessageBubble message={WELCOME_MESSAGE} theme={theme} />}
      {!showWelcome && messages.length === 0 && (
        <div style={{ textAlign: 'center', color: '#888', fontSize: '14px', marginTop: '40px' }}>
          {STRINGS.empty}
        </div>
      )}
      {messages.map((m) => (
        <MessageBubble key={m.id} message={m} theme={theme} onRetry={onRetry} />
      ))}
      {ackState !== 'none' && (
        <div
          style={{
            textAlign: 'center',
            fontSize: '11.5px',
            color: '#999',
            padding: '2px 8px 8px',
          }}
        >
          {ackState === 'online' ? STRINGS.ackOnline : STRINGS.ackOffline}
        </div>
      )}
      {aiThinking && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: '8px',
          }}
        >
          <div
            style={{
              padding: '8px 12px',
              borderRadius: '14px',
              backgroundColor: theme.bubbleAiBg,
              color: theme.bubbleAiText,
              fontSize: '14px',
            }}
          >
            <span style={{ opacity: 0.6 }}>•••</span>
          </div>
        </div>
      )}
    </div>
  );
}
